import React, { createContext, useContext } from "react";
import { Reservation } from "../types/Reservation";
import websocketService from "../../../services/websocket";
import { logger } from "../../../utils/logger";

interface ReservationsContextType {
  reservations: Reservation[];
  refreshReservations: () => Promise<void>;
  loadReservationsByDateRange: (
    startDate: Date,
    endDate: Date
  ) => Promise<void>;
  loading: boolean;
  error: string | null;
  wsConnected: boolean;
  wsLatency: number | null;
}

const defaultContextValue: ReservationsContextType = {
  reservations: [],
  refreshReservations: async () => {
    // Método por defecto - será sobrescrito por el provider
  },
  loadReservationsByDateRange: async () => {
    // Método por defecto - será sobrescrito por el provider
  },
  loading: false,
  error: null,
  wsConnected: false,
  wsLatency: null,
};

const ReservationsContext = createContext(defaultContextValue);

export const useReservationsContext = () => {
  const context = useContext(ReservationsContext);
  if (!context) {
    throw new Error(
      "useReservationsContext debe ser usado dentro de un ReservationsProvider"
    );
  }
  return context;
};

interface ProviderProps {
  children: React.ReactNode;
}

export const ReservationsProvider = ({ children }: ProviderProps) => {
  // 🚀 WebSocket state management
  const [wsConnected, setWsConnected] = React.useState(() =>
    websocketService.isConnected()
  );
  const [wsLatency, setWsLatency] = React.useState<number | null>(null);
  const disconnectTimerRef = React.useRef<number | null>(null);

  // 🚀 Configurar WebSocket
  React.useEffect(() => {
    // Layout mantiene la conexión WebSocket global de la pestaña.

    const checkConnectionStatus = () => {
      const connected = websocketService.isConnected();
      logger.info("🔍 ReservationsContext: checking WebSocket status", {
        connected,
        socketExists: !!websocketService.getSocket(),
      });
      setWsConnected(connected);

      // Si no está conectado y no está en proceso de conexión, intentar reconectar
      if (!connected && !websocketService.connecting) {
        logger.info(
          "ReservationsContext: WebSocket disconnected, attempting reconnect"
        );
        websocketService.connect();
      }
    };

    // Verificar estado cada 10 segundos (menos frecuente para evitar spam)
    const connectionInterval = setInterval(checkConnectionStatus, 10000);

    // Verificar estado después de un pequeño delay para permitir que la conexión se establezca
    setTimeout(checkConnectionStatus, 1000);

    // 🎧 Escuchar eventos de conexión WebSocket
    const handleWebSocketConnected = () => {
      logger.info("✅ ReservationsContext: WebSocket connected event received");
      if (disconnectTimerRef.current) {
        window.clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      setWsConnected(true);
    };

    const handleWebSocketDisconnected = () => {
      logger.warn(
        "❌ ReservationsContext: WebSocket disconnected event received"
      );
      if (disconnectTimerRef.current) {
        window.clearTimeout(disconnectTimerRef.current);
      }
      disconnectTimerRef.current = window.setTimeout(() => {
        setWsConnected(websocketService.isConnected());
        disconnectTimerRef.current = null;
      }, 1500);
    };

    window.addEventListener("websocket_connected", handleWebSocketConnected);
    window.addEventListener(
      "websocket_disconnected",
      handleWebSocketDisconnected
    );

    // 💓 Medir latencia periódicamente
    const latencyInterval = setInterval(async () => {
      try {
        const latency = await websocketService.ping();
        setWsLatency(latency);
      } catch (_error) {
        setWsLatency(null);
      }
    }, 30000); // Cada 30 segundos

    return () => {
      clearInterval(connectionInterval);
      clearInterval(latencyInterval);
      if (disconnectTimerRef.current) {
        window.clearTimeout(disconnectTimerRef.current);
      }
      window.removeEventListener(
        "websocket_connected",
        handleWebSocketConnected
      );
      window.removeEventListener(
        "websocket_disconnected",
        handleWebSocketDisconnected
      );
    };
  }, []);

  // 🚀 Función para cargar reservas por rango de fechas (usando React Query)
  const loadReservationsByDateRange = async (
    _startDate: Date,
    _endDate: Date
  ) => {
    logger.debug(
      "loadReservationsByDateRange called - month hooks handle reservations"
    );
  };

  const value = {
    reservations: [],
    refreshReservations: async () => {},
    loadReservationsByDateRange,
    loading: false,
    error: null,
    wsConnected,
    wsLatency,
  };

  return (
    <ReservationsContext.Provider value={value}>
      {children}
    </ReservationsContext.Provider>
  );
};
